import { ChangeEventHandler, HTMLInputTypeAttribute } from 'react';
import Image from 'next/image';
import { H6 } from 'components/Text';
import { Passenger } from 'interfaces/booking';
import { useTranslation } from 'next-i18next/pages';

interface ICustomInputProps {
  containerClassName?: string;
  className?: string;
  value?: string | null;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onEnter?: () => void;
  type?: HTMLInputTypeAttribute;
  icon?: any;
  placeholder?: string;
  multiple?: boolean;
  id?: string;
  showLabel?: boolean;
  labelClassName?: string;
  labelTitle?: string;
  name?: string;
  checked?: boolean;
  customBgColor?: string;
  textPadding?: string;
  error?: string | null;
  onBlur?: (name: keyof Passenger | any, value?: any) => void;
  autoComplete?: string;
  newsLetterRef?: any;
}

const CustomInput: React.FC<ICustomInputProps> = ({
  containerClassName,
  className,
  value,
  onChange,
  onEnter,
  type,
  icon,
  id,
  labelClassName,
  labelTitle,
  name,
  customBgColor,
  textPadding,
  error,
  onBlur,
  newsLetterRef,
  autoComplete,
  checked = false,
  placeholder = '',
  multiple = false,
  showLabel = false,
}) => {
  const { t } = useTranslation();
  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter' && onEnter) {
      onEnter();
    }
  };

  const renderBgColor = () => {
    if (!value || value.length === 0) {
      if (customBgColor) return customBgColor;
      if (error) return 'input-error bg-tt-verylight-purple';
      return 'bg-tt-verylight-purple ';
    } else {
      return 'bg-white border border-tt-verylight-purple disable-outline';
    }
  };

  const renderBorderColor = () => {
    if (error) return 'input-error';

    return 'input-purple-hover disable-outline';
  };

  return (
    <>
      {showLabel && (
        <label
          className={`${labelClassName} block tracking-wide text-sm text-tt-light-gray-purple mb-2 self-center`}
          htmlFor={id}>
          {labelTitle}
        </label>
      )}

      <div className={`${containerClassName} relative`}>
        {icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-2">
            {/* React components has this property */}
            {icon.$$typeof ? (
              <span>{icon}</span>
            ) : (
              <Image
                src={icon}
                className="h-7 self-center justify-center w-min"
                alt=""
              />
            )}
          </div>
        )}
        <input
          id={id}
          ref={newsLetterRef}
          value={value || ''}
          name={name}
          onChange={onChange}
          onBlur={() => onBlur && onBlur(name as keyof Passenger, value)}
          onKeyDown={onKeyDown}
          type={type}
          autoComplete={autoComplete}
          checked={checked}
          placeholder={placeholder}
          multiple={multiple}
          className={`${className} text-sm ${
            textPadding || 'pl-6 py-4'
          } ${renderBorderColor()} hover:-m-[1px] focus:-m-[1px] rounded w-full placeholder-tt-light-purple
          ${renderBgColor()}`}
        />
        {error && <H6 className="text-tt-error-red mt-1">{t(`${error}`)}</H6>}
      </div>
    </>
  );
};

export default CustomInput;
